import { Color, Group, LineBasicMaterial, Vector3 } from "three";
import { simpleSizeFormatter } from "oc-threejs/objects/rulers/simpleSizeFormatter";
import * as Scenes from "oc-threejs/Scenes";
import { LengthRuler, LengthRulerOptions } from "oc-threejs/objects/rulers/LengthRuler";
import { v3 } from "oc-threejs/short3";
import * as Maths3 from "oc-threejs/Maths3";
import { colorFromStyle } from "../commons/Utils";

declare const _: { keys(o: object): string[] };
declare const I18N: (s: string) => string;

interface BoxRulerOptions extends Partial<LengthRulerOptions> {
  rulerOffset?: Vector3;
}

/**
 * Stellt LengthRuler um eine Box für ungefähr die folgende Kameraperspektive dar:
 *
 *              xtop
 *              ---
 *             +---+
 *    ztop  / /   /| |  yright
 *           +---+ +
 *   yleft | |   |/  /  zbottom
 *           +---+
 *            ---
 *          xbottom
 *
 * Beispiel: Der obere Lengthruler ist dann xtop.
 *
 * Beispielhafte Extra-Definition eines Rulers für oben:
 *
 *     boxruler.description.xtop = {
 *       show: true,                              // Soll angezeigt werden ja/nein
 *       gridPoints: [v3(0, 1, 1), v3(1, 1, 1)],  // Von welchem Punkt der Box bis zu welchem Punkt der Box soll gemessen werden?
 *       rulerOffset: v3(0, 0.2, 0),              // Wie weit soll die Anzeige (Text und dazugehörige Linie) nach x, y, z verschoben werden?
 *       textOffset: v3(0, 0.02, 0),              // Wie weit soll der Text von der Anzeige nach x, y, z verschoben werden?
 *       normal: v3(0, 0, -1),                    // In welche Richtung sollen die "Ärmchen" der Anzeige zeigen?
 *       offsets: [0, 0, 0.1],                    // Verschiebung der einzelnen Linien-Teilstücke
 *       offsetsFactor: 1,                        // Sollen die Offsets umgedreht werden?
 *       textQuaternion: Maths3.quaternion.zero,  // Drehrichtung des Texts
 *       fontColor: "#333333",                    // Textfarbe
 *     };
 */
export class BoxRuler extends Group {
  CLASS_NAME = "BoxRuler";
  name = "BoxRuler";

  options: BoxRulerOptions = {};

  objects = {
    xtop: null,
    xbottom: null,
    yleft: null,
    yright: null,
    ztop: null,
    zbottom: null,
  };

  description = {
    xbottom: {
      show: true,
      gridPoints: [v3(0, 0, 1), v3(1, 0, 1)],
      rulerOffset: v3(0, 0, 0.7),
      textOffset: v3(0, 0.02, 0),
      normal: v3(0, 1, 0),
      textQuaternion: Maths3.quaternion.x270,
      offsetsFactor: -1,
    },
    yleft: {
      show: true,
      gridPoints: [v3(0, 0, 0), v3(0, 1, 0)],
      rulerOffset: v3(-0.2, 0, 0),
      textOffset: v3(-0.2, 0, 0),
      normal: v3(0, 0, 1),
      textQuaternion: Maths3.quaternion.zero,
      offsetsFactor: 1,
    },
    zbottom: {
      show: true,
      gridPoints: [v3(1, 0, 0), v3(1, 0, 1)],
      rulerOffset: v3(0.2, 0, 0),
      textOffset: v3(0, 0.2, 0),
      normal: v3(0, 1, 0),
      textQuaternion: Maths3.quaternion.zero,
      offsetsFactor: 1,
    },
  };

  überstand = false;

  updateBox(box) {
    _.keys(this.description).forEach((key) => {
      if (this.description[key].show && this.objects[key] == null) {
        const options = this.getRulerOptions(box, this.description[key]);
        this.objects[key] = new LengthRuler(options);
        this.add(this.objects[key]);
      } else if (!this.description[key].show && this.objects[key] != null) {
        Scenes.remove(this.objects[key]);
        this.objects[key] = null;
      }
      if (this.objects[key] != null) {
        const options = this.getRulerOptions(box, this.description[key]);
        this.objects[key].update(options);
      }
    });
  }

  getRulerOptions(box, description) {
    const rulerOffset =
      this.options?.rulerOffset ?? description.rulerOffset ?? v3(0, 0, this.überstand ? 1.3 : 0.7);
    const offsets = description.offsets ?? [0, 0, this.überstand ? 1.6 : 1];
    const points = description.gridPoints.map((v) => box.grid(v).add(rulerOffset));
    const colorStr =
      description.fontColor ??
      (this.überstand
        ? "#" + colorFromStyle("--color-error").getHexString()
        : "#" + colorFromStyle("--color-dark-2").getHexString());
    return {
      point1: points[0],
      point2: points[1],
      normal: description.normal,
      offsets,
      rulerOffset,
      showLines: true,
      showLinesIfLengthGreaterThen: 1,
      sizeFormatter: (size) =>
        simpleSizeFormatter()(size) +
        (this.überstand ? "\n" + I18N("geht über Grundstückslinie hinaus") : ""),
      textQuaternion: description.textQuaternion,
      textOffset: description.textOffset,
      fontHeight: 0.2,
      fontDepthTest: true,
      fontColor: colorStr,
      backgroundColor: description.backgroundColor ?? null,
      negative: false,
      textAsSprite: false,
      onTap: null,
      moveTextUpinHeightMultiplies: 0,
      textCanvasCallback: undefined,
      pixelPerUnit: 500,
      createMeshMaterial: () => new LineBasicMaterial({ color: new Color(colorStr) }),
      ...this.options,
    };
  }
}
